import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  FooterAction,
  GroupedPanelList,
  IconButton,
  Item,
  SearchField,
  TextField,
} from "@deere/isg.component-library";
import {
  IconAdd,
  IconChevronRight,
  IconMapLayers,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
} from "@deere/icons/new";
import { scaleGeoPointsToSvg } from "../utils/scaleGeoPoints";
import useApiCall from "../../hooks/useApiCall";
import TickingTime from "../../components/TickingTime";
import { getAutonomyLogNavigatorState } from "../../../modules/navigatorStateHelper";

import "../panels/welcomePanel/WelcomePanel.css";
import { useMap } from "@vis.gl/react-google-maps";
import { useDeferredFitBounds } from "../../hooks/useDeferredFitBounds";

// Used for the polygon icon for each jobsite item
interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}
const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

type Sites = {
  item: Item;
  data: any;
};

interface ShapeWithType {
  points: any[];
  zoneType?: string;
}

// Maps element/zone types to fill & stroke colors, matching the map rendering
function getColorsFromZoneType(zoneType?: string): { fill: string; stroke: string } {
  switch (zoneType) {
    case "NAVIGABLE_PATH":
      return { fill: "#0066ec4D", stroke: "#0066ec" };
    case "WINDROW":
      return { fill: "#7700c74D", stroke: "#7700c7" };
    case "STATIC_ROUTE":
      return { fill: "#00FFC34D", stroke: "#00FFC3" };
    case "RESTRICTED_ZONE":
      return { fill: "#e429294D", stroke: "#8b1717" };
    case "FREE_SPACE_ZONE":
      return { fill: "#33bd3d4D", stroke: "#1c6521" };
    case "BOUNDARY":
      return { fill: "rgba(255,0,255,0.1)", stroke: "rgb(255,0,255)" };
    case "OI_OBJECT":
      return { fill: "rgba(0,4,255,0.3)", stroke: "rgb(0,4,255)" };
    case "WORK_ZONE":
      return { fill: "rgba(255,136,0,0.3)", stroke: "rgb(255,136,0)" };
    default:
      return { fill: "#FF00FF4D", stroke: "#FF00FF" };
  }
}

function getShapeData(shapeObjects: ShapeWithType[]) {
  // Collect all points from all shapes for bounds calculation
  const allPoints: any[] = [];
  shapeObjects.forEach((s) => allPoints.push(...s.points));

  if (allPoints.length === 0) {
    return { geometry: undefined, mapbounds: null };
  }

  // Create bounds for map selection
  const bounds = new google.maps.LatLngBounds();
  allPoints.forEach((point: any) => {
    bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
  });

  // Calculate global bounds for uniform scaling across all shapes
  const allLatValues = allPoints.map((p: any) => p.location.latitude);
  const allLngValues = allPoints.map((p: any) => p.location.longitude);
  const globalBounds = {
    latMin: Math.min(...allLatValues),
    latMax: Math.max(...allLatValues),
    lngMin: Math.min(...allLngValues),
    lngMax: Math.max(...allLngValues),
  };

  // Render each shape with its own color based on zoneType
  const polygons = shapeObjects
    .map((shape, index) => getPolygon(shape.points, globalBounds, index, shape.zoneType))
    .filter(Boolean);

  const geometry = polygons.length > 0 ? (
    <svg width="32" height="32" viewBox="0 0 200 200">
      {polygons}
    </svg>
  ) : undefined;

  return {
    geometry,
    mapbounds: bounds,
  };
}

// Generate icon for a single session's shapes
function getSessionShapeIcon(sessionShapeObjects: ShapeWithType[]): React.JSX.Element | undefined {
  if (sessionShapeObjects.length === 0) {
    return undefined;
  }

  // Collect all points for calculating bounds
  const allPoints: any[] = [];
  sessionShapeObjects.forEach((shape) => {
    allPoints.push(...shape.points);
  });

  if (allPoints.length === 0) {
    return undefined;
  }

  // Calculate bounds from all session points
  const allLatValues: number[] = [];
  const allLngValues: number[] = [];
  allPoints.forEach((point: any) => {
    allLatValues.push(point.location.latitude);
    allLngValues.push(point.location.longitude);
  });

  const globalBounds = {
    latMin: Math.min(...allLatValues),
    latMax: Math.max(...allLatValues),
    lngMin: Math.min(...allLngValues),
    lngMax: Math.max(...allLngValues),
  };

  // Generate polygons for this session
  const polygons = sessionShapeObjects
    .map((shape, index) => getPolygon(shape.points, globalBounds, index, shape.zoneType))
    .filter(Boolean); // Filter out null polygons

  if (polygons.length === 0) {
    return undefined;
  }

  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      {polygons}
    </svg>
  );
}

function getPolygon(
  shapePoints: any,
  globalBounds: { latMin: number; latMax: number; lngMin: number; lngMax: number },
  index: number = 0,
  zoneType?: string,
) {
  // Check if shapePoints is valid and has points
  if (!shapePoints || !Array.isArray(shapePoints) || shapePoints.length === 0) {
    return null;
  }

  const padding = 10;
  const viewBox = 200;
  const canvasSize = viewBox - 2 * padding;

  // Uniform scale preserving real-world aspect ratio (matches scaleGeoPointsToSvg logic)
  const latRange = globalBounds.latMax - globalBounds.latMin;
  const lngRange = globalBounds.lngMax - globalBounds.lngMin;
  const avgLat = (globalBounds.latMin + globalBounds.latMax) / 2;
  const cosLat = Math.cos((avgLat * Math.PI) / 180);

  // Real-world extents in comparable units
  const xExtent = lngRange * cosLat;
  const yExtent = latRange;

  // Degenerate case: all points coincide
  if (xExtent === 0 && yExtent === 0) {
    const center = viewBox / 2;
    const pt = `${center},${center}`;
    const colors = getColorsFromZoneType(zoneType);
    return (
      <polygon key={index} points={pt} stroke={colors.stroke} fill={colors.fill} strokeWidth={2} />
    );
  }

  // Single uniform scale factor based on the larger extent
  const scale = canvasSize / Math.max(xExtent, yExtent);

  // Offsets to center the smaller dimension
  const xOffset = padding + (canvasSize - xExtent * scale) / 2;
  const yOffset = padding + (canvasSize - yExtent * scale) / 2;

  // Scale points using uniform scale for consistent aspect ratio
  const points = shapePoints
    .map((point: any) => {
      if (!point?.location?.longitude || !point?.location?.latitude) {
        return null;
      }
      const x = (point.location.longitude - globalBounds.lngMin) * cosLat * scale + xOffset;
      // Invert latitude so north (higher lat) is at the top
      const y = (globalBounds.latMax - point.location.latitude) * scale + yOffset;
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(" ");

  // Don't render if no valid points
  if (!points || points.length === 0) {
    return null;
  }

  // Use zone-type-based colors matching the map
  const colors = getColorsFromZoneType(zoneType);

  return (
    <polygon key={index} points={points} stroke={colors.stroke} fill={colors.fill} strokeWidth={2} />
  );
}

type InputFormat = {
  [vehicleType: string]: {
    [vehicleModel: string]: {
      [vehicleName: string]: {
        data: object;
      };
    };
  };
};

type OutputFormat = {
  [vehicleIndex: string]: {
    vehicleName: string;
    vehicleType: string;
    vehicleModel: string;
    data: object;
  };
};

function getStringTime(unixTime: number) {
  let formattedTime = "";
  if (unixTime) {
    const elapsed = Math.floor(unixTime / 1000); // Get elapsed time in seconds
    const days = Math.floor(elapsed / (3600 * 24));
    const hours = Math.floor((elapsed % (3600 * 24)) / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (days > 0) {
      formattedTime = `${days} days ${hours} hr`;
    } else if (hours > 0) {
      formattedTime = `${hours} hr ${minutes} min`;
    } else if (minutes > 0) {
      formattedTime = `${minutes} min ${seconds} sec`;
    } else {
      formattedTime = `${seconds} sec`;
    }
  } else {
    // formattedTime = `0 sec`;
    // console.log("Error, unix time unknown: ", unixTime);
    formattedTime = `0 sec`;
  }
  return formattedTime;
}

function JobSiteList({
  setCurJobsiteIcon,
  orgName,
  setCurrent,
  setShowAllVehicles,
  OrgData,
  curJobsite,
  setCurrJobsiteSessionIcons,
}: {
  setCurJobsiteIcon: (newIcon: any) => void;
  orgName: string;
  setCurrent: (item: Item, i: number, sessionIcons: { [key: string]: React.JSX.Element }) => void;
  setShowAllVehicles: (setBoolean: boolean) => void;
  OrgData: Object;
  curJobsite: string;
  setCurrJobsiteSessionIcons: (sessionIcons: { [key: string]: React.JSX.Element }) => void;
}) {
  const [activeItem, setActiveItem] = useState(-1); //Highlighted item in list
  const [activeSites, setActiveSites] = useState<Sites[]>([]); //holds active jobsites
  const [inactiveSites, setInactiveSites] = useState<Sites[]>([]); //hods inactive jobsites
  const [filteredActiveSites, setFilteredActiveSites] = useState<Sites[]>([...activeSites]); //holds active filtered jobsites
  const [filteredInactiveSites, setFilteredInactiveSites] = useState<Sites[]>([...inactiveSites]); //holds inactive filtered jobsites
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [newJobsiteName, setNewJobsiteName] = useState<string>("");
  const [newSessionName, setNewSessionName] = useState<string>("");
  const { execute, isLoading } = useApiCall({
    successMessage: "Jobsite created successfully",
    successTitle: "Jobsite Created",
    errorAsToast: true,
  });
  const [lastUpdateTime, setLastUpdateTime] = useState(0); // Track the last update time
  const [numberOfMachines, setNumberOfMachines] = useState<number>(0);
  const [jobsiteAutonomyState, setJobsiteAutonomyState] = useState<string>("Off");
  const [jobsiteAutonomyStateTime, setJobsiteAutonomyStateTime] = useState<string>("");

  const map = useMap("mainMap"); // Get the map instance
  const deferredFitBounds = useDeferredFitBounds();

  useEffect(() => {
    if (!curJobsite) {
      setActiveItem(-1);
    }
  }, [curJobsite]);

  function updateSites(data: Object): { activeSites: Sites[]; inactiveSites: Sites[] } {
    //takes organization data and returns list of items for the Panel List to display
    if (!data || !map) {
      return { activeSites: [], inactiveSites: [] };
    }
    const activeSites: Sites[] = [];
    const inactiveSites: Sites[] = [];
    let count = 0;
    // Sort by jobsite key so row order is stable across full-state dataUpdates.
    const sortedEntries = Object.entries(data || {}).sort(([a], [b]) => a.localeCompare(b));
    for (const [key, value] of sortedEntries) {
      let jobsiteShapePoints: any[] = [];
      let jobsiteShapeObjects: ShapeWithType[] = []; // Shape objects with zoneType for proper coloring
      let jobsiteEquipmentBounds = new google.maps.LatLngBounds();
      let hasEquipment = false;
      const sessionIcons: { [key: string]: React.JSX.Element } = {};

      let numberOfMachinesTemp = 0;
      let currentStateTemp = "off";
      let currentStateTimeTemp = 0;
      for (const [sessionKey, sessionValue] of Object.entries(value)) {
        // console.log("iterate over  sessionKey, sessionValue ", sessionKey, sessionValue);
        let sessionShapePoints: any[] = [];
        let sessionShapeObjects: ShapeWithType[] = []; // Collect shape objects for this session
        if ((sessionValue as any)?.Shape) {
          for (const [shapeKey, shapeValue] of Object.entries((sessionValue as any)?.Shape)) {
            if ((shapeValue as any)?.points) {
              const shapeObj: ShapeWithType = { points: (shapeValue as any).points, zoneType: (shapeValue as any).zoneType };
              jobsiteShapeObjects.push(shapeObj);
              sessionShapeObjects.push(shapeObj);
              jobsiteShapePoints = [...jobsiteShapePoints, ...(shapeValue as any).points];
              sessionShapePoints = [...sessionShapePoints, ...(shapeValue as any).points];
            }
          }
        }
        if (sessionValue !== null) {
            for (const [vehicleType, vehicle] of Object.entries(sessionValue as any)) {
              // verify that the element is a vehicle
              const capitalLetterCount = (vehicleType.match(/[A-Z]/g) || []).length;
              if (capitalLetterCount == 3) {
                // Item is a vehicle
                // Iterate through each Vehicle Model
                for (const vehicleModel in vehicle as any) {
                  const vehicleNames = (vehicle as any)[vehicleModel];
                  // Iterate through each Vehicle Name
                  for (const vehicleName in vehicleNames) {
                    numberOfMachinesTemp = numberOfMachinesTemp + 1;
                    const data = vehicleNames[vehicleName];

                    let thisLat = null;
                    let thisLng = null;
                    try {
                      if (
                        data?.MachineState !== undefined &&
                        data?.MachineState?.GlobalPose !== undefined
                      ) {
                        thisLat = data?.MachineState?.GlobalPose?.location?.latitude;
                        thisLng = data?.MachineState?.GlobalPose?.location?.longitude;
                      } else if (data.front_axle_center.GlobalPose !== undefined) {
                        thisLat = data?.front_axle_center?.GlobalPose?.location?.latitude;
                        thisLng = data?.front_axle_center?.GlobalPose?.location?.longitude;
                      }
                    } catch (error) {
                      console.log(error);
                    }

                    if (thisLat !== null && thisLng !== null) {
                      jobsiteEquipmentBounds.extend(
                        new google.maps.LatLng(thisLat + 0.0005, thisLng + 0.0005),
                      );
                      jobsiteEquipmentBounds.extend(
                        new google.maps.LatLng(thisLat - 0.0005, thisLng - 0.0005),
                      );
                    }
                    hasEquipment = true;

                    let firstAutonomyTime = 0;
                    firstAutonomyTime = (data as any)?.autonomyLog?.[
                      Object.keys((data as any)?.autonomyLog).length - 2
                    ]?.timestamp;

                    // Updating the jobsite autonomy state
                    let thisAutonomyState = (data as any)?.autonomyLog?.[
                      Object.keys((data as any)?.autonomyLog).length - 1
                    ];
                    // console.log("thisAutonomyState:", thisAutonomyState);
                    // console.log("thisAutonomyState?.[0]:", thisAutonomyState?.["0"]);
                    // console.log("thisAutonomyState?.[state]:", thisAutonomyState?.["state"]);
                    let thisAutonomyTime = Math.floor(Date.now() - thisAutonomyState?.timestamp); // Get elapsed time in seconds
                    // console.log("thisAutonomyTime: ",thisAutonomyTime);
                    const navState = getAutonomyLogNavigatorState(thisAutonomyState);
                    if (
                      (thisAutonomyState?.["0"] !== "1" &&
                        thisAutonomyState?.["0"] !== "2" &&
                        thisAutonomyState?.["0"] !== undefined) ||
                      (thisAutonomyState?.["0"] == undefined &&
                        navState == undefined)
                    ) {
                      // Autonomy state is off
                      if (currentStateTemp == "off") {
                        currentStateTemp = "off";
                        if (thisAutonomyTime > currentStateTimeTemp) {
                          currentStateTimeTemp = thisAutonomyTime;
                        }
                      }
                    } else if (
                      thisAutonomyState?.["0"] == "1" ||
                      navState == "READY"
                    ) {
                      // Autonomy state is ready
                      if (currentStateTemp == "off") {
                        currentStateTemp = "ready";
                        currentStateTimeTemp = thisAutonomyTime;
                      } else if (currentStateTemp == "ready") {
                        currentStateTemp = "ready";
                        if (thisAutonomyTime > currentStateTimeTemp) {
                          currentStateTimeTemp = thisAutonomyTime;
                        }
                      }
                    } else if (navState == "INTERRUPTED") {
                      // Autonomy state is paused
                      if (currentStateTemp == "off" || currentStateTemp == "ready") {
                        currentStateTemp = "paused";
                        currentStateTimeTemp = thisAutonomyTime;
                      } else if (currentStateTemp == "paused") {
                        currentStateTemp = "paused";
                        if (thisAutonomyTime > currentStateTimeTemp) {
                          currentStateTimeTemp = thisAutonomyTime;
                        }
                      }
                    } else {
                      // Autonomy state is working
                      if (currentStateTemp !== "working") {
                        currentStateTemp = "working";
                        currentStateTimeTemp = thisAutonomyTime;
                      } else if (currentStateTemp == "working") {
                        currentStateTemp = "working";
                        if (thisAutonomyTime > currentStateTimeTemp) {
                          currentStateTimeTemp = thisAutonomyTime;
                        }
                      }
                    }
                  }
                }
              }
            }
        }

        // Generate session-specific icon from its shapes
        if (sessionShapeObjects.length > 0) {
          const sessionIcon = getSessionShapeIcon(sessionShapeObjects);
          if (sessionIcon) {
            sessionIcons[sessionKey] = sessionIcon;
          }
        }
      }
      setNumberOfMachines(numberOfMachinesTemp);
      setJobsiteAutonomyState(currentStateTemp);
      setJobsiteAutonomyStateTime(getStringTime(currentStateTimeTemp));
      let currentStateTimestamp = currentStateTimeTemp > 0 ? Date.now() - currentStateTimeTemp : 0;
      // Checking if no shapes exist, look for machines
      for (const [sessionKey, sessionValue] of Object.entries(value)) {
        let sessionShapePoints: any[] = [];
        let sessionShapeObjects2: ShapeWithType[] = []; // Collect shape objects for this session
        if ((sessionValue as any)?.Shape) {
          for (const [shapeKey, shapeValue] of Object.entries((sessionValue as any)?.Shape)) {
            if ((shapeValue as any)?.points) {
              // Only add if not already added in first pass
              if (!jobsiteShapeObjects.some((obj) => obj.points === (shapeValue as any).points)) {
                jobsiteShapeObjects.push({ points: (shapeValue as any).points, zoneType: (shapeValue as any).zoneType });
              }
              sessionShapeObjects2.push({ points: (shapeValue as any).points, zoneType: (shapeValue as any).zoneType });
              jobsiteShapePoints = [...jobsiteShapePoints, ...(shapeValue as any).points];
              sessionShapePoints = [...sessionShapePoints, ...(shapeValue as any).points];
            }
          }
        }

        // Generate session-specific icon if not already created
        if (sessionShapeObjects2.length > 0 && !sessionIcons[sessionKey]) {
          const sessionIcon = getSessionShapeIcon(sessionShapeObjects2);
          if (sessionIcon) {
            sessionIcons[sessionKey] = sessionIcon;
          }
        }
      }
      //console.log("------- jobsiteShapePoints----", JSON.stringify(jobsiteShapePoints));
      let jobsiteShapeData = getShapeData(jobsiteShapeObjects);

      let tempBounds = null;
      if (jobsiteShapePoints.length > 0) {
        tempBounds = jobsiteShapeData.mapbounds;
      } else if (hasEquipment) {
        tempBounds = jobsiteEquipmentBounds;
      }

      let tempAutonomyStatus = (
        <>
          <div
            style={{
              position: "relative",
              top: "4px",
              left: "-116px",
              width: "200px",
              height: "60px",
              display: "flex",
              flexDirection: "column",
              paddingLeft: "10px",
            }}
          >
            <b style={{ height: "16px" }}>{key}</b>
            <div style={{ height: "18px" }}>
              {/* {numberOfMachines.toString()} Machines on Jobsite */}
              {numberOfMachinesTemp.toString()} Machines on Jobsite
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              {/* <div style={{ color: "#00B600", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
                                <StatusEquipmentWorking size={20} sx={{ marginRight: '4px' }} />
                                6 min 15 sec
                            </div> */}

              {currentStateTemp == "off" && (
                <div
                  style={{
                    color: "#000000",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <StatusAutonomyOff size={20} sx={{ marginRight: "4px" }} />
                  {/* {"Off "} */}
                  <TickingTime timestamp={currentStateTimestamp} />
                </div>
              )}
              {currentStateTemp == "ready" && (
                <div
                  style={{
                    color: "#00B600",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <StatusAutonomyReady size={20} />
                  {/* {"Ready "} */}
                  <TickingTime timestamp={currentStateTimestamp} />
                </div>
              )}
              {currentStateTemp == "paused" && (
                <div
                  style={{
                    color: "rgb(255, 120, 0)",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                  <StatusEquipmentIdle size={20} sx={{ marginRight: "4px" }} />
                  {/* {"Paused "} */}
                  <TickingTime timestamp={currentStateTimestamp} />
                </div>
              )}
              {currentStateTemp == "working" && (
                <div
                  style={{
                    color: "rgb(6, 138, 252)",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <StatusAutonomyActive size={20} sx={{ marginRight: "-10px" }} />
                  <StatusEquipmentWorking size={20} sx={{ marginRight: "4px" }} />
                  {/* {"6 min 15 sec"} */}
                  {/* {"Working "} */}
                  <TickingTime timestamp={currentStateTimestamp} />
                </div>
              )}
            </div>
          </div>

          {/* <div style={{ display: "flex", height: "58px", marginRight: "35px", alignItems: "center" }}>
                        <div style={{ color: "#00B600", fontWeight: "bold", display: "flex", alignItems: "center" }}>
                            <StatusAutonomyActive size={20} sx={{ marginRight: '-10px' }} />
                            <StatusEquipmentWorking size={20} sx={{ marginRight: '4px' }} />
                            {"6 min 15 sec"}
                        </div>
                    </div> */}
        </>
      );

      const entry: Item = {
        id: count.toString(),
        // title: key,
        title: "",
        // subtitle: "4 Machines on Jobsite",
        subtitle: "",
        icon: (
          <>
            {jobsiteShapePoints.length > 0 ? (
              jobsiteShapeData.geometry
            ) : (
              <Polygon
                points="10,10 190,10 190,190 10,190"
                fill="#afafaf50"
                stroke="#797979ff"
                strokeWidth={2}
              />
            )}
          </>
        ),
        // icon: <Polygon points="10,10 190,10 160,190 40,190" fill="#ffd9004d" stroke="#FF00FF" strokeWidth={2} />,
        extraContent: tempAutonomyStatus,
      };

      if (currentStateTemp == "off") {
        inactiveSites.push({
          item: entry,
          data: {
            title: key,
            mapbounds: tempBounds,
            sessionsIcons: sessionIcons,
          },
        });
      } else {
        activeSites.push({
          item: entry,
          data: {
            title: key,
            mapbounds: tempBounds,
            sessionsIcons: sessionIcons,
          },
        });
      }

      count++;
    }
    // console.log("activeSites", activeSites, "inactiveSites", inactiveSites);

    setActiveSites(activeSites); // Update active sites
    setFilteredActiveSites(activeSites); // Update filtered active sites

    setInactiveSites(inactiveSites); // Update inactive sites
    setFilteredInactiveSites(inactiveSites); // Update filtered inactive sites

    return { activeSites, inactiveSites };
  }

  useEffect(() => {
    const { activeSites: freshActive, inactiveSites: freshInactive } = updateSites(OrgData) ?? { activeSites: [], inactiveSites: [] };

    const allSites = [...freshActive, ...freshInactive];
    // Look up by jobsite name (curJobsite) rather than index, since
    // active/inactive grouping can shift indices when data changes.
    const selectedSite = curJobsite
      ? allSites.find((s) => s.data.title === curJobsite)
      : allSites[activeItem];
    if (selectedSite) {
      setCurJobsiteIcon(selectedSite.item.icon);
      setCurrJobsiteSessionIcons(selectedSite.data.sessionsIcons);
    }
  }, [OrgData]);

  const handleItemSelect = useCallback(
    (data: any, item: Item, i: number) => {
      //handles click on panel list item
      console.log(" handleItemSelect Item selected:");
      setActiveItem(i);
      setCurrent(
        {
          ...item,
          title: data.title,
        },
        // item
        i,
        data.sessionsIcons,
      );
      setCurJobsiteIcon(item.icon);
      setShowAllVehicles(false);
      // console.log((data.mapBounds as google.maps.LatLngBounds).toSpan());
      if (data.mapbounds !== null) {
        deferredFitBounds(data.mapbounds);
      }
    },
    [activeItem, setCurrent, setCurJobsiteIcon, deferredFitBounds],
  );

  const groups = useMemo(
    () => [
      {
        //creates group of items for panel list

        title: "Active (" + filteredActiveSites.length + ")",
        items: filteredActiveSites.map((site, i) => ({
          ...site.item,
          active: i === activeItem,
          onClick: () => handleItemSelect(site.data, site.item, i),
          // icon: <Polygon points="10,10 190,10 160,190 40,190" fill="#FF00FF4D" stroke="#FF00FF" strokeWidth={2} />,
          secondaryAction: (
            <div>
              <IconButton
                className="layerButton"
                icon={
                  <span>
                    <IconMapLayers size={24} primary={"#666666"} />
                  </span>
                }
                variant="borderless"
              />
              <IconButton
                className="chevronButton"
                icon={
                  <span>
                    <IconChevronRight size={24} />
                  </span>
                }
                variant="borderless"
                onClick={() => handleItemSelect(site.data, site.item, i)}
              />
            </div>
          ),
          // extraContent: <div style={{ height: "58px" }} />
        })),
      },
      {
        title: "Inactive (" + filteredInactiveSites.length + ")",
        items: filteredInactiveSites.map((site, i) => ({
          ...site.item,
          active: i + filteredActiveSites.length === activeItem,
          onClick: () => handleItemSelect(site.data, site.item, i + filteredActiveSites.length),
          // icon: <LocationPin />,
          secondaryAction: (
            <div>
              <IconButton
                className="layerButton"
                icon={
                  <span>
                    <IconMapLayers size={24} primary={"#666666"} />
                  </span>
                }
                variant="borderless"
              />
              <IconButton
                className="chevronButton"
                icon={
                  <span>
                    <IconChevronRight size={24} />
                  </span>
                }
                variant="borderless"
                onClick={() =>
                  handleItemSelect(site.data, site.item, i + filteredActiveSites.length)
                }
              />
            </div>
          ),
          // extraContent: <div style={{ height: "58px" }} />
        })),
      },
    ],
    [activeItem, activeSites, inactiveSites, filteredInactiveSites, filteredInactiveSites],
  );

  function searchSites(e: any) {
    //search filter, just checks if search query is included

    setFilteredActiveSites(
      activeSites.filter((site: Sites) =>
        site.item.title.toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()),
      ),
    );
    setFilteredInactiveSites(
      inactiveSites.filter((site: Sites) =>
        site.item.title.toLocaleLowerCase().includes(e.target.value.toLocaleLowerCase()),
      ),
    );
  }

  function publishNewJobsite() {
    let orgString = orgName;
    if (orgName === "") {
      orgString = "orchestratorORG";
    }
    return fetch(`/API/Save/AddJobsite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        currentTopicString: orgString + "_" + newJobsiteName + "_" + newSessionName + "/",
      }),
    });
  }

  function isValidString(input: string): boolean {
    const regex = /^[a-zA-Z0-9 ]*$/; // Regex to allow a-z, A-Z, 0-9, and spaces
    return regex.test(input);
  }

  const footerActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      onClick: (): void => {
        setShowUpload(false);
      },
    },
    {
      title: "Upload",
      variant: "primary",
      disabled: newJobsiteName === "" || newSessionName === "" || isLoading,
      loading: isLoading,
      onClick: async (): Promise<void> => {
        if (!isValidString(newJobsiteName) || !isValidString(newSessionName)) {
          return;
        }
        const success = await execute(() => publishNewJobsite());
        if (success) {
          setNewJobsiteName("");
          setNewSessionName("");
          setShowUpload(false);
        }
      },
    },
  ];

  return (
    <div className="JobsiteList">
      <div className="SearchEntry">
        <SearchField
          color="primary"
          onBlur={function noRefCheck() {}}
          onChange={searchSites}
          onFocus={function noRefCheck() {}}
          onInvalid={function noRefCheck() {}}
          onKeyDown={function noRefCheck() {}}
          onKeyUp={function noRefCheck() {}}
          size="medium"
          style={{ width: "340px", height: "32px" }}
        />
        <IconButton
          icon={<IconAdd />}
          onFocusVisible={function noRefCheck() {}}
          title="Add a Jobsite"
          style={{ width: "32px", height: "32px" }}
          onClick={() => {
            setShowUpload(true);
          }}
        />
      </div>
      <div className="PanelList">
        <GroupedPanelList groups={groups} />
      </div>
      <Dialog
        footerActions={footerActions}
        closeHandler={() => {
          setShowUpload(false);
        }}
        open={showUpload}
        title={"Add Jobsite"}
        width="sm"
      >
        {(newJobsiteName === "" || newSessionName === "") && (
          <>
            <Alert variant="outlined" severity="info">
              Set a new Jobsite and Session name
            </Alert>
            <br />
          </>
        )}
        {(!isValidString(newJobsiteName) || !isValidString(newSessionName)) && (
          <>
            <Alert variant="outlined" severity="error">
              Only use Numbers, Letters, Spaces
            </Alert>
            <br />
          </>
        )}
        <TextField
          error={!isValidString(newJobsiteName)}
          label={"New Jobsite Name"}
          onChange={(event) => setNewJobsiteName(event.target.value)}
          placeholder="Enter a jobsite name"
          value={newJobsiteName}
          style={{ width: "100%", padding: "0px 0px 0px 0px" }}
        />
        <TextField
          error={!isValidString(newSessionName)}
          label={"New Session Name"}
          onChange={(event) => setNewSessionName(event.target.value)}
          placeholder="Enter a session name"
          value={newSessionName}
          style={{ width: "100%", padding: "16px 0px 0px 0px" }}
        />
      </Dialog>
    </div>
  );
}

export default JobSiteList;
